import { HostingDisplayName } from '@atlassian/mpac-types';
import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import { ExternalLink } from '@atlassian/mpac-ui';
import React from 'react';
import { valueForHosting } from '../../../../../../utils';
import { addIf } from './pricingFAQs.utils';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export const FAQKey = {
  HOW_PRICING_WORKS: 'how-pricing-works',
  PRICE_DETERMINATION: 'pricing-determination',
  DATA_CENTER_LICENSING: 'dc-licensing',
  DATA_CENTER_COMPATIBILITY: 'dc-compatibility',
  ALTERNATE_LICENSING: 'alternate-licenses',
  FREE_TRIAL_EXTENSION: 'free-trial-extension',
  LEGACY_SERVER_PRICING: 'legacy-server-licensing'
};
const howPricingWorksCloud = () => /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx("p", {
    children: "Cloud apps are sold as a monthly or annual subscription. You are eligible for support and automatic version updates as long as your subscription is active."
  }), /*#__PURE__*/_jsx("p", {
    children: "When your subscription renews each month, you are automatically billed for apps based on the number of users in your instance."
  })]
});
const howPricingWorksServer = serverSubscriptionApp => serverSubscriptionApp ? /*#__PURE__*/_jsx("p", {
  children: "Server products and apps are hosted on your servers. This app is sold as an annual subscription. You are eligible for support and version updates as long as your subscription is active."
}) : /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx("p", {
    children: "Server products and apps are hosted on your servers. This app is sold as a perpetual license, and the purchase price includes 12 months of maintenance (support and version updates)."
  }), /*#__PURE__*/_jsxs("p", {
    children: ["You can", ' ', /*#__PURE__*/_jsx(ExternalLink, {
      href: "https://www.atlassian.com/purchase/renew",
      children: "renew maintenance"
    }), ' ', "after 12 months at 50% of the current purchase price. You can", ' ', /*#__PURE__*/_jsx(ExternalLink, {
      href: "https://www.atlassian.com/purchase/upgrade",
      children: "upgrade"
    }), " the tier of your Atlassian product and app licenses at any time. Upgrade prices are calculated based on Atlassian's formula (", /*#__PURE__*/_jsx(ExternalLink, {
      href: "https://www.atlassian.com/licensing/jira-software#serverlicenses-3",
      children: "view example"
    }), ")."]
  })]
});
const howPricingWorksDC = () => /*#__PURE__*/_jsx("p", {
  children: "Data Center apps are sold as an annual subscription. You are eligible for support and version updates as long as your subscription is active."
});
export const howPricingWorksFAQ = _ref => {
  let {
    hosting,
    serverSubscriptionApp,
    byAtlassian
  } = _ref;
  return {
    question: "How does ".concat(HostingDisplayName[hosting].toLowerCase(), " app pricing work?"),
    key: FAQKey.HOW_PRICING_WORKS,
    answer: /*#__PURE__*/_jsxs(_Fragment, {
      children: [valueForHosting(hosting)({
        [AtlassianProductHostingType.Cloud]: howPricingWorksCloud(),
        [AtlassianProductHostingType.Server]: howPricingWorksServer(serverSubscriptionApp),
        [AtlassianProductHostingType.DataCenter]: howPricingWorksDC()
      }), !byAtlassian && /*#__PURE__*/_jsx("p", {
        children: "If app pricing changes after your initial purchase, there's a 60-day grandfathering period during which you can renew based on the old pricing."
      })]
    })
  };
};
const getCompatibleJiraProductName = (isJSMCompatible, isJiraSoftwareCompatible) => {
  if (isJSMCompatible && isJiraSoftwareCompatible) {
    return 'Jira Service Management and Jira Software';
  } else if (isJSMCompatible) {
    return 'Jira Service Management';
  } else if (isJiraSoftwareCompatible) {
    return 'Jira Software';
  }
};
export const howToDeterminePricingFAQ = _ref2 => {
  let {
    hosting,
    isJSMCompatible,
    isJiraSoftwareCompatible,
    parentCompatibleProduct
  } = _ref2;
  const compatibleJiraProductName = getCompatibleJiraProductName(isJSMCompatible, isJiraSoftwareCompatible);
  const answerCloud = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("p", {
      children: ["Apps are billed based on the number of users in your Atlassian product.", ' ', (parentCompatibleProduct === null || parentCompatibleProduct === void 0 ? void 0 : parentCompatibleProduct.id) === 'jira' ? /*#__PURE__*/_jsxs(_Fragment, {
        children: ["Jira Cloud apps are priced based on the maximum users of the Jira products on your instance. For example, if you have Jira Software (50 users) and Jira Service Management (10 agents) on the same instance, you pay the 50-user price for apps.", (isJSMCompatible || isJiraSoftwareCompatible) && /*#__PURE__*/_jsxs("p", {
          children: [/*#__PURE__*/_jsx("br", {}), " Note: While this app has features specific to ", compatibleJiraProductName, ", the app is technically available across the whole Jira instance. Therefore the above guidelines for licensing across maximum users still apply."]
        })]
      }) : (parentCompatibleProduct === null || parentCompatibleProduct === void 0 ? void 0 : parentCompatibleProduct.id) === 'confluence' ? /*#__PURE__*/_jsx(_Fragment, {
        children: "Confluence Cloud apps are priced based on the number of Confluence users on your instance. For example, if you have Confluence Cloud (25 users), you pay the 25-user price for apps."
      }) : null]
    }), /*#__PURE__*/_jsx("p", {
      children: "The pricing structure for cloud apps is as follows:"
    }), /*#__PURE__*/_jsxs("ul", {
      children: [/*#__PURE__*/_jsx("li", {
        children: "Monthly subscriptions with up to 10 Atlassian product users are billed at a flat rate price."
      }), /*#__PURE__*/_jsx("li", {
        children: "Monthly subscriptions with more than 10 users are billed per user, starting at the first user."
      }), /*#__PURE__*/_jsx("li", {
        children: "Monthly subscriptions with more than 100 users are billed per user. The table above provides example pricing at various user levels."
      })]
    }), /*#__PURE__*/_jsx("p", {
      children: "Annual subscriptions may offer a discount depending on the number of users purchased."
    })]
  });
  const answerServerDC = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("p", {
      children: "Apps are billed based on the number of users in your Atlassian product."
    }), ' ', (parentCompatibleProduct === null || parentCompatibleProduct === void 0 ? void 0 : parentCompatibleProduct.id) === 'jira' ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs("p", {
        children: ["For Jira 7.0 or later, the app tier should match the maximum tier of the licensed Jira products on your instance. For example, if you're running Jira Software (500 users) and", ' ', "Jira Service Management (25 agents) on the same instance, you should purchase the 500-user tier for apps."]
      }), /*#__PURE__*/_jsx("p", {
        children: "For versions of Jira prior to 7.0, the app tier should match the licensed user tier for Jira. Even if fewer users want to use the app than your Jira license, the two licenses should match exactly."
      }), (isJSMCompatible || isJiraSoftwareCompatible) && /*#__PURE__*/_jsxs("p", {
        children: ["Note: While this app has features specific to ", compatibleJiraProductName, ", the app is technically available across the whole Jira instance. Therefore the above guidelines for the license tier still apply."]
      })]
    }) : /*#__PURE__*/_jsx(_Fragment, {
      children: "The app tier should match the licensed user tier of the Atlassian product. For example, if you have a Confluence license for 500 users, you should purchase the 500-user tier for apps. Even if fewer users want to use the app than your Atlassian product license, the two licenses should match exactly."
    })]
  });
  return {
    question: "How do I determine my ".concat(HostingDisplayName[hosting].toLowerCase(), " pricing?"),
    key: FAQKey.PRICE_DETERMINATION,
    answer: valueForHosting(hosting)({
      [AtlassianProductHostingType.Cloud]: answerCloud,
      [AtlassianProductHostingType.Server]: answerServerDC,
      [AtlassianProductHostingType.DataCenter]: answerServerDC
    })
  };
};
export const howToInstallInDataCenterFAQ = _ref3 => {
  let {
    hosting,
    hasDataCenterVersion
  } = _ref3;
  return {
    question: 'Can I install this app in a data center product?',
    key: FAQKey.DATA_CENTER_COMPATIBILITY,
    answer: /*#__PURE__*/_jsxs(_Fragment, {
      children: [hosting === AtlassianProductHostingType.DataCenter || hasDataCenterVersion ? /*#__PURE__*/_jsx("p", {
        children: "Yes, this app has a Data Center approved version. If you're using a Data Center product, you should install the Data Center version of the app."
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("p", {
          children: "This app does not have a Data Center approved version. Only apps meeting a set of performance requirements for large, clustered environments are approved for Data Center."
        }), /*#__PURE__*/_jsx("p", {
          children: "However, while server apps are built for single-server deployments, many customers successfully install them in Data Center products. If you're using a Data Center product, Atlassian recommends you contact the vendor to determine compatibility at scale."
        })]
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(ExternalLink, {
          href: "https://www.atlassian.com/licensing/data-center-approved-apps",
          children: "Learn more about Data Center approved apps"
        })
      })]
    })
  };
};
export const dataCenterLicenseFAQ = _ref4 => {
  let {
    hosting,
    hasDataCenterVersion
  } = _ref4;
  return {
    question: "What type of license do I need if I'm using this app in a data center product?",
    key: FAQKey.DATA_CENTER_LICENSING,
    answer: /*#__PURE__*/_jsxs(_Fragment, {
      children: [hosting === AtlassianProductHostingType.DataCenter || hasDataCenterVersion ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("p", {
          children: "Because this app has a Data Center approved version, you should purchase a Data Center license for the app."
        }), /*#__PURE__*/_jsx("p", {
          children: "If you already own a server license for this app, you can continue using the server license in your Data Center product for a limited period of time."
        })]
      }) : /*#__PURE__*/_jsx("p", {
        children: "Because this app does not have a Data Center approved version, you should purchase a Server license if you decide to install this app in a Data Center product."
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(ExternalLink, {
          href: "https://www.atlassian.com/licensing/data-center-approved-apps",
          children: "Learn more about Data Center licensing"
        })
      })]
    })
  };
};
export const alternateLicensesFAQ = _ref5 => {
  let {
    hosting
  } = _ref5;
  const answerServerDC = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("p", {
      children: ["For ", HostingDisplayName[hosting].toLowerCase(), " apps, academic licenses are available at a 50% discount if you have an academic license for your Atlassian product."]
    }), hosting === AtlassianProductHostingType.DataCenter ? /*#__PURE__*/_jsxs("p", {
      children: ["Open-source licenses are not available for Data Center apps but we do offer Data Center Community subscriptions (covering both product and Marketplace apps). If you wish to apply for the Atlassian Community license then please refer to this", ' ', /*#__PURE__*/_jsx(ExternalLink, {
        href: "https://www.atlassian.com/software/views/community-license-request",
        children: "documentation"
      }), "."]
    }) : /*#__PURE__*/_jsxs("p", {
      children: ["Community and open-source licenses are available for ", HostingDisplayName[hosting], " apps.", ' ', /*#__PURE__*/_jsx(ExternalLink, {
        href: "https://www.atlassian.com/licensing/marketplace#licensingandpricing-7",
        children: "Learn more about community and open source licenses"
      }), "."]
    })]
  });
  return {
    question: "Do you offer academic, community, or open-source licenses for ".concat(HostingDisplayName[hosting].toLowerCase(), " apps?"),
    key: FAQKey.ALTERNATE_LICENSING,
    answer: valueForHosting(hosting)({
      [AtlassianProductHostingType.Cloud]: /*#__PURE__*/_jsxs("p", {
        children: ["Yes. Academic, community, and open source licenses are available to qualifying organizations. See our", ' ', /*#__PURE__*/_jsx(ExternalLink, {
          href: "https://www.atlassian.com/licensing/purchase-licensing#pricing-discounts",
          children: "Purchasing & Licensing FAQ"
        }), ' ', "for more information."]
      }),
      [AtlassianProductHostingType.Server]: answerServerDC,
      [AtlassianProductHostingType.DataCenter]: answerServerDC
    })
  };
};
export const freeTrialExtensionFAQ = _ref6 => {
  let {
    hosting
  } = _ref6;
  const answerServerDC = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("p", {
      children: ["For ", HostingDisplayName[hosting], " apps, you can extend your app trial up to 5 times - in other words, for up to six months. Extend your trial by generating a new evaluation license key from Atlassian Marketplace \u2014"]
    }), /*#__PURE__*/_jsxs("ul", {
      children: [/*#__PURE__*/_jsxs("li", {
        children: ["Click ", /*#__PURE__*/_jsx("b", {
          children: "Try it free"
        }), " and you'll be directed to generate a new license."]
      }), /*#__PURE__*/_jsxs("li", {
        children: [' ', "Paste this license key into the app listing in UPM from your Atlassian product, and you're all set."]
      })]
    })]
  });
  return {
    question: 'Can I extend my free trial?',
    key: FAQKey.FREE_TRIAL_EXTENSION,
    answer: valueForHosting(hosting)({
      [AtlassianProductHostingType.Cloud]: /*#__PURE__*/_jsx("p", {
        children: "For cloud apps, you cannot extend your free evaluation period. All cloud apps are immediately subscribed by a user, and we provide a free evaluation period. This is a minimum of 30 days and ends on the second billing cycle after you first subscribe to the app."
      }),
      [AtlassianProductHostingType.Server]: answerServerDC,
      [AtlassianProductHostingType.DataCenter]: answerServerDC
    })
  };
};
export const legacyServerLicenseFAQ = () => {
  return {
    question: 'How can I buy apps for my legacy Jira Server or Confluence Server license?',
    key: FAQKey.LEGACY_SERVER_PRICING,
    answer: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("p", {
        children: "If you own a legacy Jira Server Unlimited (100+ users) or Confluence Server Unlimited (2000+ users) license purchased in 2012 or earlier, legacy app pricing is no longer available. You have two options for app purchasing:"
      }), /*#__PURE__*/_jsxs("ul", {
        children: [/*#__PURE__*/_jsx("li", {
          children: "Purchase the app at the non-legacy Unlimited (10000+ users) tier."
        }), /*#__PURE__*/_jsx("li", {
          children: "Renew your Jira or Confluence license at a non-legacy tier, then purchase the app at the same tier."
        })]
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(ExternalLink, {
          href: "https://www.atlassian.com/licensing/marketplace",
          children: "Learn more"
        })
      })]
    })
  };
};
export default function getPaidViaAtlassianFAQs(_ref7) {
  let {
    hosting,
    isJSMCompatible,
    isJiraSoftwareCompatible,
    parentCompatibleProduct,
    serverSubscriptionApp,
    hasDataCenterVersion,
    byAtlassian
  } = _ref7;
  return [howPricingWorksFAQ({
    hosting,
    serverSubscriptionApp,
    byAtlassian
  }), howToDeterminePricingFAQ({
    hosting,
    isJSMCompatible,
    isJiraSoftwareCompatible,
    parentCompatibleProduct
  }), ...addIf(hosting !== AtlassianProductHostingType.Cloud, howToInstallInDataCenterFAQ({
    hosting,
    hasDataCenterVersion
  })), ...addIf(hosting !== AtlassianProductHostingType.Cloud, dataCenterLicenseFAQ({
    hosting,
    hasDataCenterVersion
  })), alternateLicensesFAQ({
    hosting
  }), freeTrialExtensionFAQ({
    hosting
  }), ...addIf(hosting === AtlassianProductHostingType.Server, legacyServerLicenseFAQ())];
}